import React, { Fragment, useState, useCallback, useEffect } from 'react';
import { DateTimePicker } from '@material-ui/pickers';
import { TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslate } from 'react-admin';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import { time } from '~/utils';
import { CurrencyAutocomplete, AddFilterAction } from '~/components';
import { filterChange, selectors, cleanupFilters } from '~/ducks/virtualizedList';
import { Moment } from '~/types';
import BackgroundTaskExport from '~/components/BackgroundTaskExport';
import { DealSearchFilterDto } from '~/api/backgroundTasks';
import { cleanupFormValues } from '~/utils';
import { OperationType } from '~/types/OperationType';
import { IconButton } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/RemoveCircleOutline';
import cx from 'classnames';
import { useListFilterStyles } from '~/hooks/useListFilterStyles';
import {
  SelectTransactionType,
  DEALS_EXCLUDED_OPERATION_TYPES,
} from '~/components/TransactionsHistory/components/SelectTransactionType';
import { SelectDealsStatus } from '~/components/TransactionsHistory/components/SelectDealsStatus';
import { hasCurrentLocale12HourFormat } from '~/utils/moment';

//--------------------------------------------
const resource = 'deals';

interface Props {
  cleanFilters?: boolean;
}

const Filters = (props: Props) => {
  const { cleanFilters = true } = props;
  const classes = useStyles();
  const listFilterClasses = useListFilterStyles();
  const dispatch = useDispatch();
  const translate = useTranslate();
  const t = (key: string): string => translate(`resources.deals.list.filters.${key}`);

  const filters = useSelector(selectors.getFilters<DealFilters>(resource));
  const [localValues, setLocalValues] = useState<DealFilters>(filters);
  const [addedFilters, setAddedFilters] = useState<string[]>([]);

  useEffect(() => {
    setLocalValues({ ...filters });
  }, [filters]);

  useEffect(() => {
    const activeKeys = Object.keys(filters).filter((k) => !!(filters as any)[k]);
    setAddedFilters((prev) => {
      const merged = new Set([...prev, ...activeKeys]);
      return Array.from(merged);
    });
  }, [filters]);

  const dispatchFilterChange = (name: string, value: string | Moment) => {
    dispatch(filterChange({ resource, filters: { [name]: value || null } }));
  };

  const debouncedDispatchFilterChange = useCallback(debounce(dispatchFilterChange, 500), []);

  const handleDateTimeChange = (data: Moment, inputType: string) => {
    debouncedDispatchFilterChange(inputType, data);
  };

  const debouncedHandleInput = (e: any) => {
    const { name, value } = e.target;

    setLocalValues({
      ...localValues,
      [name]: value,
    });

    debouncedDispatchFilterChange(name, value);
  };

  const handleSelectInput = (e: any) => {
    const { name, value } = e.target;
    setLocalValues({
      ...localValues,
      [name]: value,
    });

    dispatchFilterChange(name, value);
  };

  const filtersAreEmpty = isEmpty(filters);
  useEffect(() => {
    if (cleanFilters || filtersAreEmpty) {
      dispatch(cleanupFilters({ resource }));
      dispatch(
        filterChange({
          resource,
          filters: {
            createdFrom: time().subtract(1, 'months'),
            createdTo: time(),
          },
        })
      );
      setAddedFilters([]);
    }
  }, [dispatch, cleanFilters, filtersAreEmpty]);

  const hideFilter = (filter: string) => {
    const inputName = filter;
    dispatchFilterChange(inputName, '');
    setAddedFilters(addedFilters.filter((f) => f !== filter));
  };

  const allFilters = [
    'senderAccountNumber',
    'recipientAccountNumber',
    'operationType',
    'amountFrom',
    'amountTo',
    'currency',
    'status',
  ];

  const renderAddedFilter = (key: string) => {
    switch (key) {
      case 'senderAccountNumber':
        return (
          <TextField
            label={t('senderAccountNumber')}
            name='senderAccountNumber'
            value={localValues.senderAccountNumber ?? ''}
            onChange={debouncedHandleInput}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'recipientAccountNumber':
        return (
          <TextField
            label={t('recipientAccountNumber')}
            name='recipientAccountNumber'
            value={localValues.recipientAccountNumber ?? ''}
            onChange={debouncedHandleInput}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'operationType':
        return (
          <SelectTransactionType
            label={t('operationType')}
            name='operationType'
            value={localValues.operationType || ''}
            onChange={handleSelectInput}
            exclude={DEALS_EXCLUDED_OPERATION_TYPES}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'amountFrom':
        return (
          <TextField
            label={t('amountFrom')}
            onChange={debouncedHandleInput}
            value={localValues.amountFrom ?? ''}
            name='amountFrom'
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'amountTo':
        return (
          <TextField
            label={t('amountTo')}
            onChange={debouncedHandleInput}
            value={localValues.amountTo ?? ''}
            name='amountTo'
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'currency':
        return (
          <CurrencyAutocomplete
            label={t('currency')}
            onChange={debouncedHandleInput}
            value={localValues.currency}
            name='currency'
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      case 'status':
        return (
          <SelectDealsStatus
            name='status'
            value={localValues.status || ''}
            onChange={handleSelectInput}
            className={cx(listFilterClasses.formControl, listFilterClasses.formControlAdd)}
          />
        );
      default:
        return null;
    }
  };

  // Render added filters before the boolean switch
  // to ensure the switch is always at the end of the filter list
  // and to avoid missmathced filter widths
  const renderAddedFilters = () =>
    addedFilters.map((filter) => {
      const component = renderAddedFilter(filter);
      if (component) {
        return (
          <div className={listFilterClasses.flexContainer} key={filter}>
            {component}
            <IconButton
              aria-label='delete'
              className={listFilterClasses.deleteIcon}
              onClick={() => hideFilter(filter)}
            >
              <DeleteIcon />
            </IconButton>
          </div>
        );
      } else {
        return null;
      }
    });

  return (
    <Fragment>
      {filters && (
        <div className={listFilterClasses.container}>
          <div className={classes.leftSide}>
            <div className={listFilterClasses.inputsGroup}>
              <DateTimePicker
                className={cx(
                  listFilterClasses.formControl,
                  listFilterClasses.formControlLong,
                  listFilterClasses.datePickers
                )}
                format='L - LT'
                ampm={hasCurrentLocale12HourFormat()}
                margin='normal'
                label={t('from')}
                value={localValues.createdFrom}
                onChange={(date) => handleDateTimeChange(date as Moment, 'createdFrom')}
              />
              <DateTimePicker
                className={cx(
                  listFilterClasses.formControl,
                  listFilterClasses.formControlLong,
                  listFilterClasses.datePickers
                )}
                format='L - LT'
                ampm={hasCurrentLocale12HourFormat()}
                margin='normal'
                label={t('to')}
                value={localValues.createdTo}
                onChange={(date) => handleDateTimeChange(date as Moment, 'createdTo')}
              />
              {renderAddedFilters()}
            </div>
          </div>
          <div className={classes.rightSide}>
            <AddFilterAction
              filters={allFilters
                .filter((f) => !addedFilters.includes(f))
                .sort()
                .map((filterKey) => ({
                  key: filterKey,
                  label: t(filterKey),
                }))}
              onFilterSelected={(filter: string) => {
                setAddedFilters(addedFilters.concat([filter]));
              }}
            />
            <BackgroundTaskExport type='DEAL_EXPORT' queryArguments={handleFilters(localValues)} />
          </div>
        </div>
      )}
    </Fragment>
  );
};

type DealFilters = {
  operationType?: OperationType;
  createdFrom?: Moment | null;
  createdTo?: Moment | null;
  senderAccountNumber?: string;
  recipientAccountNumber?: string;
  amountFrom?: number;
  amountTo?: number;
  currency?: string;
  status?: string;
};

const handleFilters = (filters: DealFilters): DealSearchFilterDto => {
  if (!filters) return {};
  const { createdFrom, createdTo, currency, amountFrom, amountTo, ...rest } = filters;
  return cleanupFormValues({
    createdFrom: handleDate(createdFrom),
    createdTo: handleDate(createdTo),
    beneficiaryAmountCurrency: currency,
    beneficiaryAmountFrom: amountFrom,
    beneficiaryAmountTo: amountTo,
    ...rest,
  });
};

const handleDate = (date?: Moment | null) => {
  if (!date) return undefined;
  return date.toISOString();
};

const useStyles = makeStyles(() => {
  return {
    leftSide: {
      maxWidth: 'calc(100% - 250px)',
    },
    // Can change width to fit export or other buttons on the right side
    rightSide: { minWidth: 250, textAlign: 'right' },
  };
});

export default Filters;
